import { useEffect, useRef, useState } from "react";
import { Menu, Shield, ArrowLeft } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { Avatar } from "@/shared/ui/avatar";
import { useAuth } from "@/app/providers/useAuth";
import { toast } from "@/shared/lib/toast";

export interface AppHeaderProps {
  isSidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  isAdminMode?: boolean;
  isAdminAllowed?: boolean;
}

export function AppHeader({
  isSidebarCollapsed,
  onToggleSidebar,
  isAdminMode = false,
  isAdminAllowed = false,
}: AppHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { logout, displayName, roles } = useAuth();

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      if (
        menuRef.current &&
        event.target instanceof Node &&
        !menuRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen]);

  const handleModeToggle = () => {
    if (isAdminMode) {
      navigate({ to: "/home" });
    } else {
      navigate({
        to: "/admin",
        search: {
          panel: "users",
        },
      });
    }
  };

  const handleLogout = async () => {
    setIsMenuOpen(false);
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
      toast.error("Logout failed", "Please try again.");
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-carbon-950/80 backdrop-blur-lg shadow-[0_18px_42px_-28px_rgba(0,59,153,0.65)]">
      <div className="flex h-18 w-full items-center justify-between px-4 md:px-6">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onToggleSidebar}
            className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 bg-white/10 text-white transition hover:border-white/25 hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-imperial-500 md:hidden"
            aria-label={
              isSidebarCollapsed ? "Expand navigation" : "Collapse navigation"
            }
          >
            <Menu aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center gap-3">
          {isAdminAllowed ? (
            <button
              type="button"
              onClick={handleModeToggle}
              className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white/80 transition hover:border-white/20 hover:bg-white/10 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-imperial-500"
              aria-label={
                isAdminMode
                  ? "Return to operations portal"
                  : "Switch to admin mode"
              }
            >
              {isAdminMode ? (
                <>
                  <ArrowLeft aria-hidden="true" className="h-4 w-4" />
                  <span className="hidden sm:inline">Operations</span>
                </>
              ) : (
                <>
                  <Shield aria-hidden="true" className="h-4 w-4" />
                  <span className="hidden sm:inline">Admin</span>
                </>
              )}
            </button>
          ) : null}

          {/* TODO: Add help & documentation pdf if needed */}
          {/* <button
            type="button"
            className="flex h-11 w-11 items-center justify-center rounded-full"
            aria-label="Help & documentation"
          >
            <HelpCircle aria-hidden="true" className="h-5 w-5 text-white" />
          </button> */}
          <div ref={menuRef} className="relative">
            <button
              type="button"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-grid-200 bg-grid-100 text-ink-500 transition hover:border-grid-300 hover:bg-grid-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-imperial-500"
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              aria-label="Open account menu"
              onClick={() => {
                setIsMenuOpen((previous) => !previous);
              }}
            >
              <Avatar name={displayName} size="md" />
            </button>
            {isMenuOpen ? (
              <div
                role="menu"
                className="absolute right-0 mt-3 w-48 overflow-hidden rounded-xl border border-white/10 bg-carbon-900/95 p-1 text-sm text-white/80 shadow-[0_24px_48px_-28px_rgba(0,59,153,0.65)] backdrop-blur"
              >
                <div className="px-4 pb-2 pt-3 text-left text-xs text-white/60">
                  <p className="font-semibold text-white/90">{displayName}</p>
                  <p className="mt-1 text-[11px] leading-relaxed text-white/50">
                    Roles: {roles.length ? roles.join(", ") : "none"}
                  </p>
                </div>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  className="flex w-full items-center justify-between rounded-lg px-4 py-2 text-left transition hover:bg-white/10 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-imperial-500"
                >
                  Logout
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}
